import {
  PAYER_TYPES,
  RESIDENT_SORT_KEYS,
  RESIDENT_STATUSES,
  type PayerType,
  type ResidentStatus,
} from "@alm/domain";

export type SortKey = (typeof RESIDENT_SORT_KEYS)[number];
export type OptionalColumn = "dateOfBirth" | "facility" | "gender";

export const OPTIONAL_COLUMNS: { id: OptionalColumn; label: string }[] = [
  { id: "dateOfBirth", label: "Date of birth" },
  { id: "facility", label: "Facility" },
  { id: "gender", label: "Gender" },
];

export interface ResidentSearch {
  q?: string;
  /** Empty means every status. */
  status?: ResidentStatus[];
  gender?: string;
  payer?: PayerType;
  room?: string;
  minAge?: number;
  maxAge?: number;
  tags?: string[];
  sort?: SortKey;
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  cols?: OptionalColumn[];
}

/** Older saved-view tab ids, still honoured as deep links. */
const LEGACY_VIEW: Record<string, ResidentStatus[]> = {
  active: ["active"],
  "out-of-facility": ["out_of_facility_active", "out_of_facility_hold"],
  discharged: ["discharged"],
  inactive: ["inactive"],
};

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

// The search box's own text, spaces and all: it is read back on every keystroke, so trimming it
// here ate the space before the next word could be typed ("Sam Test" stopped at "Sam").
const typed = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

/** A list arrives either as a real array or as the comma-joined string an old link carries. */
const list = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === "string");
  return typeof value === "string" ? value.split(",").filter(Boolean) : [];
};

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

const whole = (value: unknown, min: number, max: number): number | undefined => {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

export function parseResidentSearch(search: Record<string, unknown>): ResidentSearch {
  const statuses = list(search.status).filter((s): s is ResidentStatus =>
    (RESIDENT_STATUSES as readonly string[]).includes(s),
  );
  const view = text(search.view);
  return {
    q: typed(search.q) ?? text(search.search),
    status: statuses.length ? statuses : view ? LEGACY_VIEW[view] : undefined,
    gender: text(search.gender),
    payer: oneOf(search.payer, PAYER_TYPES),
    room: text(search.room),
    minAge: whole(search.minAge, 0, 130),
    maxAge: whole(search.maxAge, 0, 130),
    tags: list(search.tags).length ? list(search.tags) : undefined,
    sort: oneOf(search.sort, RESIDENT_SORT_KEYS),
    dir: oneOf(search.dir, ["asc", "desc"] as const),
    page: whole(search.page, 1, 100000),
    pageSize: whole(search.pageSize, 1, 200),
    cols: list(search.cols).filter((c): c is OptionalColumn =>
      OPTIONAL_COLUMNS.some((col) => col.id === c),
    ),
  };
}

/** Whether anything narrows the list beyond the building itself. */
export function hasNarrowing(search: ResidentSearch): boolean {
  return Boolean(
    search.q ||
    search.status?.length ||
    search.gender ||
    search.payer ||
    search.room ||
    search.minAge !== undefined ||
    search.maxAge !== undefined ||
    search.tags?.length,
  );
}
